import React, {useState, useEffect, useCallback} from "react";
import {
    Dialog,
    FormControlLabel,
    Button,
    Grid, Checkbox,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

import withAuthorization from "../Session/withAuthorization";
import {compose} from "recompose";
import {appStyles} from "../../styles";
import {DateSelector, OptionSelector, TimeSelector} from "../Forms";
import {isMember, SORTIE_TASK_STATES} from "../../constants";
import {addMinutes, subMinutes} from "date-fns";
import {CheckBox, CheckBoxOutlineBlank} from "@material-ui/icons";
import {DialogActions, DialogContent, DialogTitle} from "../Dialog";
import {adjustTime, utcDate, zonedStartOfDay} from "../Session/utils";
import {SelectSortie} from "../Forms/OptionSelector";
import {AuditableChange} from "../Audit";
import {firestoreAutoId} from "../Firebase";
import FlightOptionsContext from "../Session/flightOptionsContext";

const FlightEditor = (props) => {
    const {authUser, firebase, openDate} = props;
    const [autoShowTimes, setAutoShowTimes] = useState(true);
    const [eShowTime, setEShowTime] = useState(new Date());
    const [oShowTime, setOShowTime] = useState(new Date());
    const [crTime, setCRTime] = useState(new Date());
    const [takeoffTime, setTakeoffTime] = useState(new Date());
    const [landTime, setLandTime] = useState(new Date());
    const [sortieType, setSortieType] = useState("");
    const [eventList, setEventList] = useState([])
    const flightOptions = React.useContext(FlightOptionsContext);

    const changeTakeoffTime = useCallback((time, type, defaultTimes) => {
        if (defaultTimes && flightOptions.sortieDefaults) {
            setEShowTime(subMinutes(time, flightOptions.sortieDefaults[type].eShow * 60));
            setOShowTime(subMinutes(time, flightOptions.sortieDefaults[type].oShow * 60));
            setCRTime(subMinutes(time, flightOptions.sortieDefaults[type].cr * 60));
        }
        setTakeoffTime(time);
    }, [flightOptions.sortieDefaults]);

    useEffect(() => {
        if (openDate && flightOptions.sortieDefaults) {
            const defaultSortie = Object.keys(flightOptions.sortieDefaults)[0];
            setSortieType(defaultSortie);
            const defaultSortieData = flightOptions.sortieDefaults[defaultSortie];
            if (defaultSortieData && defaultSortieData.events) {
                setEventList(Object.values(defaultSortieData.events));
            } else {
                setEventList([]);
            }
            const mtvDayStart = zonedStartOfDay(openDate);
            const mtvTakeoff = addMinutes(mtvDayStart, flightOptions.sortieDefaults[defaultSortie].takeoffTime * 60);
            changeTakeoffTime(mtvTakeoff, defaultSortie,true);
            setLandTime(addMinutes(mtvDayStart, flightOptions.sortieDefaults[defaultSortie].landTime * 60));
        }
    }, [openDate, flightOptions.sortieDefaults, changeTakeoffTime])

    const saveFlight = () => {
        const events = {}
        eventList.forEach((event) => {
            const eventTime = addMinutes(takeoffTime, event.time * 60);
            events[`${firestoreAutoId()}`] = {
                type: event.type,
                location: event.location,
                blockStart: eventTime.toISOString(),
                blockEnd: addMinutes(eventTime, event.length * 60).toISOString(),
            }
        })

        const tasks = (flightOptions.sortieTaskDefaults || []).map(sub => ({
            ...sub,
            tasks: sub.tasks.map(task => ({
                ...task,
                status: SORTIE_TASK_STATES.NOT_STARTED
            }))
        }));

        const delta = {
            autoShowTimes: autoShowTimes,
            eShowTime: adjustTime(takeoffTime, eShowTime),
            oShowTime: adjustTime(takeoffTime, oShowTime),
            crTime: adjustTime(takeoffTime, crTime),
            takeoffTime: adjustTime(takeoffTime, takeoffTime),
            landTime: adjustTime(takeoffTime, landTime, true),
            sortieType: sortieType,
            events: events,
            tasks: tasks,
        }
        AuditableChange(authUser.uid, firebase.flights(), null, delta, "Created flight")
        exitModal()
    };

    const updateEventList = (type, add) => {
        if (add) {
            const newEvent = { type: type, time: 0, length: 1, location: '' };
            setEventList([...eventList, newEvent]);
        } else {
            setEventList(eventList.filter(event => event.type !== type));
        }
    }

    const updateSortieType = (type) => {
        setSortieType(type);
        const defaultSortieData = flightOptions.sortieDefaults[type];
        if (defaultSortieData && defaultSortieData.events) {
            setEventList(Object.values(defaultSortieData.events));
        } else {
            setEventList([]);
        }
        const mtvDayStart = zonedStartOfDay(takeoffTime);
        const mtvTakeoff = addMinutes(mtvDayStart, flightOptions.sortieDefaults[type].takeoffTime * 60);
        changeTakeoffTime(mtvTakeoff, type,true);
        setLandTime(addMinutes(mtvDayStart, flightOptions.sortieDefaults[type].landTime * 60));
    };

    const updateDefaults = () => {
        if (!autoShowTimes) {
            changeTakeoffTime(takeoffTime, sortieType, true);
        }
        setAutoShowTimes(!autoShowTimes);
    }

    const exitModal = () => {
        props.toggle();
    }

    if (!flightOptions.sortieDefaults || !flightOptions.sortieEvents) {
        return null;
    }

    return (
        <>
            <Dialog open={!!openDate}>
                <DialogTitle  id="customized-dialog-title" onClose={exitModal}>
                    Add Flight
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <DateSelector
                                label={"Date"}
                                value={takeoffTime}
                                onChange={() => {/*TODO: this could be editable but would have to update all the times*/}}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <OptionSelector value={sortieType} selectType={SelectSortie} onChange={updateSortieType} />
                        </Grid>
                        <Grid item xs={4}>
                            <TimeSelector label={"Takeoff Time"} value={takeoffTime}
                                          onChange={(value) => changeTakeoffTime(utcDate(value), sortieType, autoShowTimes)} />
                        </Grid>
                        <Grid item xs={4}>
                            <TimeSelector label={"Land Time"} value={landTime} onChange={(value) => setLandTime(utcDate(value))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color={"primary"}
                                        checked={autoShowTimes}
                                        icon={<CheckBoxOutlineBlank fontSize="small"/>}
                                        checkedIcon={<CheckBox fontSize="small"/>}
                                        onClick={updateDefaults}
                                    />
                                }
                                label={"Use Default Preflight Times"}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TimeSelector label={"O Show Time"} value={oShowTime} onChange={(value) => setOShowTime(utcDate(value))}
                                          disabled={autoShowTimes} />
                        </Grid>
                        <Grid item xs={4}>
                            <TimeSelector label={"E Show Time"} value={eShowTime} onChange={(value) => setEShowTime(utcDate(value))}
                                          disabled={autoShowTimes} />
                        </Grid>
                        <Grid item xs={4}>
                            <TimeSelector label={"Crew Ready"} value={crTime} onChange={(value) => setCRTime(utcDate(value))}
                                          disabled={autoShowTimes} />
                        </Grid>
                        {Object.keys(flightOptions.sortieEvents).map((keyName, keyIndex) =>
                            {
                                const exists = eventList.some(v => v.type === keyName)
                                return(
                                <Grid item key={keyIndex} xs={4}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                key={keyIndex}
                                                color={"primary"}
                                                checked={exists}
                                                onClick={() => updateEventList(keyName, !exists) }
                                            />
                                        }
                                        label={flightOptions.sortieEvents[keyName].name} />
                                </Grid>
                                )
                            }
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="default"
                        data-dismiss="modal"
                        type="button"
                        onClick={exitModal}
                    >
                        Close
                    </Button>
                    <Button
                        color="primary"
                        type="button"
                        onClick={saveFlight}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default compose(
    withStyles(appStyles),
    withAuthorization(isMember),
)(FlightEditor);
